import { Next, UserInfo } from '@/types';
import { Service, UserMock } from '@cnamts/accessmaster-jwt/types';
import { Route } from 'vue-router';
import userInfoService from '@/services/Security/UserInfo/userInfoService';
import jwtTokenManager from '@/services/Security/jwtTokenManager';
import AccessMaster, { TOKEN_COOKIE_NAME } from '@/accessMaster';
import router from '@/config/router/router';
import projectInfoService from '@/services/Navigation/ProjectInfoService';
import appStateUpdator from '@/store/AppStateUpdator';
import { useSurveyCampaignStore } from '@/store/SurveyCampaignStore';

declare const ACCESSMASTER_MOCK: string;

const accessmasterManager = {
	/**
	 * Manage the accessmaster authentication
	 * @param to
	 * @param from
	 * @param next
	 */
	checkAuthentication(to: Route, from: Route, next: Next) {
		if (accessmasterManager.isLogged() && !userInfoService.hasUserInfo()) {
			this.getUserInfo(next);
		}
		// The user just logged in,
		// store his informations
		if (to.hash.includes('#access_token')) {
			this.connectUser(next);
		} else if (this.isLogged() && userInfoService.hasUserInfo()) {
			AccessMaster.decorateAxiosInstance();
			// The user is already logged in (cookie), display app
			userInfoService.updateAppState(userInfoService.getUserInfo(), true, true);
		} else if ('false' === ACCESSMASTER_MOCK) {
			AccessMaster.login();
		}
	},
	/**
	 * Check if the user is logged with accessmaster
	 */
	isLogged() {
		return AccessMaster.isLoggedIn();
	},
	/**
	 * Reconnect the user when the token expired
	 * @param to
	 * @param next
	 */
	refreshUser(to: Route, next: Next) {
		// We need to check this after otherwise the previous condition
		// will fail and it will fall to "user already logged in"
		appStateUpdator.updateLoaded(false);
		// If the user just logged out, don't do anything,
		// or if an error has occurred
		if ('loggedOut' === to.name || 'loginError' === to.name) {
			appStateUpdator.updateIsAccessMaster(false);
			next();
			// If not in mock mode, and on route for authent
		} else if ('false' === ACCESSMASTER_MOCK) {
			// If not in mock mode, log in the user
			AccessMaster.login();
		} else {
			// else if in mock mode, redirect the user to the form
			next({ name: 'accessmaster' });
		}
	},
	/**
	 * Authenticate the user on the server accessmaster
	 * @param next
	 */
	connectUser(next: Next) {
		// if the user has user info, we delete them for refresh
		if (userInfoService.hasUserInfo()) {
			userInfoService.deleteUserInfo();
		}
		// CallBack after login to save user info
		const saveUserInfoCallBack = () => {
			this.getUserInfo(next);
		};
		// After the redirection from the auth server
		// we save the token and call userinfo
		AccessMaster.loginCallback(saveUserInfoCallBack);
	},
	/**
	 * Get the user info with the token
	 * @param next
	 */
	getUserInfo(next: Next) {
		AccessMaster.getUserInfo(
			(data: object) => {
				const user = data as UserMock;
				const userInfo: UserInfo = {
					lastname: user.family_name,
					firstname: user.given_name,
					// on ne récupère que les noms de services en tant que roles
					roles: user.services ? user.services.map((service: Service) => service.nom) : [],
					organizationCode: user.code_organisme,
					mail: null,
					phoneAreaCode: null,
					mobilePhone: null,
					projectUsers: null,
					authorizedAuthentication: null
				};

				userInfoService.saveUserInfo(userInfo);

				userInfoService.updateAppState(userInfo, true, true);
				// Redirect to home to clean token in URL
				next({ name: 'home' });
			},
			(error: object) => {
				if (error) {
					userInfoService.updateAppState(null, false, false);
					next({ name: 'loginError' });
				}
			}
		);
	},
	/**
	 * Déconnecte l'utilisateur courant et supprime le JSON Web Token enregistré en cookie
	 */
	logout() {
		const surveyCampaignStore = useSurveyCampaignStore();

		jwtTokenManager.deleteToken(TOKEN_COOKIE_NAME);
		AccessMaster.logout();
		userInfoService.updateAppState(null, false, false);
		userInfoService.deleteUserInfo();
		projectInfoService.deleteProjectStores();
		surveyCampaignStore.clearSelectedCampaign();

		router.push({
			name: 'login'
		});
	}
};

export default accessmasterManager;
